// src/pages/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import addressService from '../services/addressService';
import AddressCard from '../components/address/AddressCard';
import AddressForm from '../components/address/AddressForm';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Loading from '../components/common/Loading';
import { getErrorMessage, getInitials } from '../utils/helpers';

/**
 * Profile Page Component
 * 
 * Features:
 * - User profile information
 * - Edit profile
 * - Manage delivery addresses
 * - Account settings
 * - Change password
 * - Logout
 */
const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  
  // Profile edit state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phone: '',
  });
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Initialize profile data
    if (user) {
      setProfileData({
        fullName: user.fullName || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
    
    fetchAddresses();
  }, [isAuthenticated, user, navigate]);
  
  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const data = await addressService.getUserAddresses();
      setAddresses(data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddAddress = async (addressData) => {
    try {
      await addressService.createAddress(addressData);
      setShowAddressForm(false);
      fetchAddresses();
      alert('Address added successfully');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };
  
  const handleEditAddress = async (addressData) => {
    try {
      await addressService.updateAddress(editingAddress.id, addressData);
      setEditingAddress(null);
      fetchAddresses();
      alert('Address updated successfully');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };
  
  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }
    
    try {
      await addressService.deleteAddress(addressId);
      fetchAddresses();
      alert('Address deleted successfully');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };
  
  const handleSetDefaultAddress = async (addressId) => {
    try {
      await addressService.setDefaultAddress(addressId);
      fetchAddresses();
      alert('Default address updated');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };
  
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };
  
  if (loading) {
    return <Loading fullScreen message="Loading profile..." />;
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Account</h1>
          <p className="text-gray-600 mt-2">
            Manage your profile and settings
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6">
              {/* User Avatar */}
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-3xl font-bold">
                    {getInitials(user?.fullName)}
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900">{user?.fullName}</h3>
                <p className="text-sm text-gray-600">{user?.email}</p>
              </div>
              
              {/* Navigation Tabs */}
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg font-medium transition-colors
                    ${activeTab === 'profile' 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  👤 Profile
                </button>
                
                <button
                  onClick={() => setActiveTab('addresses')}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg font-medium transition-colors
                    ${activeTab === 'addresses' 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  📍 Addresses
                </button>
                
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg font-medium transition-colors
                    ${activeTab === 'orders' 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  📦 Orders
                </button>
                
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`
                    w-full text-left px-4 py-3 rounded-lg font-medium transition-colors
                    ${activeTab === 'settings' 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  ⚙️ Settings
                </button>
              </nav>
              
              {/* Logout Button */}
              <div className="mt-6">
                <Button
                  variant="danger"
                  fullWidth
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    Profile Information
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingProfile(!isEditingProfile)}
                  >
                    {isEditingProfile ? 'Cancel' : 'Edit Profile'}
                  </Button>
                </div>
                
                {isEditingProfile ? (
                  <form className="space-y-4">
                    <Input
                      label="Full Name"
                      value={profileData.fullName}
                      onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                    />
                    
                    <Input
                      label="Email"
                      type="email"
                      value={profileData.email}
                      disabled
                      helperText="Email cannot be changed"
                    />
                    
                    <Input
                      label="Phone"
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      maxLength={10}
                    />
                    
                    <div className="flex gap-3">
                      <Button onClick={() => setIsEditingProfile(false)}>
                        Save Changes
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsEditingProfile(false);
                          setProfileData({
                            fullName: user?.fullName || '',
                            email: user?.email || '',
                            phone: user?.phone || '',
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Full Name</label>
                      <p className="text-gray-900 mt-1">{user?.fullName}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">Email</label>
                      <p className="text-gray-900 mt-1">{user?.email}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">Phone</label>
                      <p className="text-gray-900 mt-1">{user?.phone || 'Not provided'}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">Account Type</label>
                      <p className="text-gray-900 mt-1">{user?.role === 'ROLE_USER' ? 'Customer' : user?.role}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Addresses Tab */}
            {activeTab === 'addresses' && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    Delivery Addresses
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddressForm(true)}
                  >
                    + Add New Address
                  </Button>
                </div>
                
                {addresses.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600 mb-4">No addresses saved yet</p>
                    <Button onClick={() => setShowAddressForm(true)}>
                      Add Your First Address
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {addresses.map(address => (
                      <AddressCard
                        key={address.id}
                        address={address}
                        onEdit={(addr) => setEditingAddress(addr)}
                        onDelete={handleDeleteAddress}
                        onSetDefault={handleSetDefaultAddress}
                        showActions
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  My Orders
                </h2>
                
                <div className="text-center py-12">
                  <p className="text-gray-600 mb-4">View all your orders</p>
                  <Button onClick={() => navigate('/orders')}>
                    Go to Orders
                  </Button>
                </div>
              </div>
            )}
            
            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  Account Settings
                </h2>
                
                <div className="space-y-6">
                  {/* Change Password */}
                  <div className="border-b border-gray-200 pb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Change Password
                    </h3>
                    <Button variant="outline">
                      Change Password
                    </Button>
                  </div>
                  
                  {/* Notifications */}
                  <div className="border-b border-gray-200 pb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Notifications
                    </h3>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                        <span className="ml-3 text-gray-700">Email notifications for orders</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                        <span className="ml-3 text-gray-700">Promotional emails</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Delete Account */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Danger Zone
                    </h3>
                    <Button variant="danger" onClick={() => alert('Delete account functionality')}>
                      Delete Account
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Add Address Modal */}
      <Modal
        isOpen={showAddressForm}
        onClose={() => setShowAddressForm(false)}
        title="Add New Address"
        size="lg"
      >
        <AddressForm
          onSubmit={handleAddAddress}
          onCancel={() => setShowAddressForm(false)}
        />
      </Modal>
      
      {/* Edit Address Modal */}
      <Modal
        isOpen={!!editingAddress}
        onClose={() => setEditingAddress(null)}
        title="Edit Address"
        size="lg"
      >
        <AddressForm
          address={editingAddress}
          onSubmit={handleEditAddress}
          onCancel={() => setEditingAddress(null)}
        />
      </Modal>
    </div>
  );
};

export default ProfilePage;